/**
 * **宣言 → GUI** の薄い橋（Issue #523）。**ページの持ち物**で、`duelEditor/` には入れない
 * （あちらの外部パッケージは react と `stage/three/` の three だけ、という境界を保つ）。
 *
 * テーマを作る流れは「**AI が初稿を書く → 人が数値を手で詰める → その結果がテーマに残る**」。
 * ここが担うのは真ん中の「詰める」ところだけで、**何が触れるかはテーマの `schema` が決める**
 * ので、テーマを足せば GUI は勝手に増える。
 *
 * lil-gui は**動的 import**（`/duel-editor` は `App.js` に静的 import ＝ main バンドルなので、
 * 素朴に入れると全員に乗る）。純関数だけここでテストし、**lil-gui の DOM は見ない**。
 */

/**
 * 部品の名前 → フォルダの見出し。**見出しはテーマの宣言（`theme.parts` の `label`）から引く**（Issue #526）。
 *
 * 以前はここに部品名 → 見出しの**手書きの表**があったが、`core/theme::STAGE_PARTS` が既に `label` を
 * 持っていて二重だった。演出（#525）が入ると部品は **イベント × 層** で数十になるので、
 * 手書きの表は必ず腐る。**宣言を 1 か所の正本にする。**
 *
 * @param parts `theme.parts`（`{ part, label, space }` の配列）
 */
export function partLabel(part, parts = []) {
  const hit = (parts ?? []).find((p) => p?.part === part);
  return hit?.label ?? part ?? 'その他';
}

/**
 * **部品の中の並び**（Issue #523）。編集ソフトと同じく **共通 → 固有**:
 * トランスフォーム（`core/transform`）→ 飾り（`core/decor`）→ そのテーマ固有の宣言。
 * 綴りは core の `TRANSFORM_GROUP` / `DECOR_GROUP` と揃える（ここが GUI 側の一次情報）。
 */
export const GROUP_ORDER = Object.freeze(['トランスフォーム', '飾り']);

export const TRANSFORM_GROUP = GROUP_ORDER[0];

/**
 * 宣言 → **フォルダごとのコントローラの一覧**（純関数）。見出しは `parts` から引く（Issue #526）。
 * 並びは宣言の順（テーマが書いた順に出る）が、**共通の組は部品の頭**に置く（`GROUP_ORDER`）。
 */
export function guiFields(schema, parts = []) {
  const out = [];
  Object.entries(schema ?? {}).forEach(([key, f]) => {
    const part = f?.part ?? 'その他';
    let group = out.find((g) => g.part === part);
    if (!group) { group = { part, label: partLabel(part, parts), fields: [] }; out.push(group); }
    group.fields.push({ key, ...f });
  });
  const rank = (f) => {
    const i = GROUP_ORDER.indexOf(f?.group);
    return i < 0 ? GROUP_ORDER.length : i;
  };
  // **安定なソート**（同じ組の中では宣言の順のまま）
  out.forEach((g) => { g.fields = g.fields.map((f, i) => [f, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([f]) => f); });
  return out;
}

/**
 * **既定と違う値だけ**（テーマに貼るのはこれ）。
 * 触っていないものまで書き出すと、テーマの既定を変えたときに古い値が勝ってしまう。
 */
export function changedValues(values, defaults) {
  const out = {};
  Object.entries(values ?? {}).forEach(([k, v]) => {
    if (!(k in (defaults ?? {}))) return;
    if (!Object.is(v, defaults[k])) out[k] = v;
  });
  return out;
}

/** テーマのファイルに貼れる形（`style` のブロック） */
export function exportText(values, defaults) {
  const changed = changedValues(values, defaults);
  const body = Object.entries(changed)
    .map(([k, v]) => `  ${k}: ${typeof v === 'string' ? JSON.stringify(v) : v},`)
    .join('\n');
  return Object.keys(changed).length
    ? `// themes/<name>/ の style に混ぜる（既定と違うものだけ）\n{\n${body}\n}`
    : '// 既定のまま（触った値はありません）';
}

/** localStorage のキー（テーマごと） */
export const storeKey = (themeName) => `duelEditor.themeStyle.${themeName}`;

/** 読み込む（壊れていたら空。**宣言にあるものだけ**通すのは呼ぶ側の `resolveStyle`） */
export function loadStyle(themeName, storage = window.localStorage) {
  try {
    const raw = storage?.getItem?.(storeKey(themeName));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** 書き込む（**既定と違うものだけ**。空なら消す） */
export function saveStyle(themeName, values, defaults, storage = window.localStorage) {
  try {
    const changed = changedValues(values, defaults);
    if (Object.keys(changed).length) storage?.setItem?.(storeKey(themeName), JSON.stringify(changed));
    else storage?.removeItem?.(storeKey(themeName));
  } catch {
    /* プライベートウィンドウなどで書けなくても、詰める作業そのものは続けられる */
  }
}

/**
 * lil-gui を組み立てる。**呼ぶ側が `dispose()` する**。
 *
 * @param GUI 動的 import した lil-gui の既定 export
 * @param el 置き場（ここに `gui.domElement` を入れる）
 * @param schema テーマの宣言
 * @param parts `theme.parts`（フォルダの見出しの出どころ。Issue #526）
 * @param values 今の値（**この object を lil-gui が直接書き換える**）
 * @param onChange 触られたら呼ぶ（ページが state に写す）
 */
export function buildGui(GUI, el, { schema, parts = [], values, onChange }) {
  const gui = new GUI({ container: el, title: 'テーマを詰める', width: 300 });
  const folders = {};
  guiFields(schema, parts).forEach((group) => {
    const folder = gui.addFolder(group.label);
    folder.close();
    folders[group.part] = folder;
    group.fields.forEach((f) => {
      if (f.type === 'color') folder.addColor(values, f.key).name(f.label ?? f.key);
      else if (f.type === 'select') folder.add(values, f.key, Object.fromEntries(f.options.map(([l, v]) => [l, v]))).name(f.label ?? f.key);
      else if (f.type === 'number') folder.add(values, f.key, f.min, f.max, f.step).name(f.label ?? f.key);
      else folder.add(values, f.key).name(f.label ?? f.key);
    });
  });
  gui.onChange(() => onChange?.({ ...values }));
  return { gui, folders };
}
